import React, { useState } from "react";
import { Spin, Select, Modal, message } from "antd";
import payIcon from "../../assets/UiElements/pay.png";
import addBeneIcon from "../../assets/UiElements/addbene.png";
import claimIcon from "../../assets/UiElements/claim.png";
import { useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import {
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useDeleteBeneficiary } from "../../hooks/beneficiaries/useDeleteBeneficiary";
import { AddBeneficiaryModal } from "../../components/AddBeneficiaryModal";
import FutureValueCalculatorModal from "../../components/FutureValueCalculatorModal";
import ClaimsModal from "../../components/ClaimsModal";
import WithdrawalRequestModal from "../../components/WithdrawalRequestModal";
import { useGetCertificate } from "../../hooks/useGetCertificate";
import { useGetInvestmentReport } from "../../hooks/useGetInvestmentReport";
import { useGetPremiumStatement } from "../../hooks/useGetPremiumStatement";
import { useGetPolicyDocument } from "../../hooks/useGetPolicyDocument";
import { usePolicyReports } from "../../hooks/usePolicyReports";
import SimpleFactsheetModal from "../../components/SimpleFactsheetModal";
import { getUserDetail, getUserToken, notifyUser } from "../../util/util";
import { api } from '../../lib/api-client';
import {
  useInitiatePensionPayment,
  usePensionPaymentHistory,
} from "../../hooks/usePayment";
import { PaymentOptionsModal } from "../../components/PaymentOptionsModal";
import { PaymentHistoryModal } from "../../components/PaymentHistoryModal";
import RecommendUpgrade from "../../components/RecommendUpgrade";
import {
  normalizeBeneficiaries,
  extractBeneficiaryListFromResponse,
  formatBeneficiaryPercentage,
  getInitials,
} from "../../util/beneficiaryUtils";
import { useGetMVestBeneficiaries } from "../../hooks/mvest/useMVest";
import { useAllPension } from "../../hooks/products/pension/useAllPension";
import { useSelectScheme } from "../../hooks/products/pension/useSelectScheme";

function formatCurrency(value) {
  if (!value && value !== 0) return "GHS 0.00";
  // Check if value is negative
  const isNegative = Number(value) < 0;
  // Remove any existing currency sign, commas, and minus signs, then format
  const num = Math.abs(Number(String(value).replace(/[^\d.]/g, "")));
  if (isNaN(num)) return `GHS ${value}`;
  // Format: GHS -34,000.00 (minus after GHS)
  const sign = isNegative ? "-" : "";
  return `GHS ${sign}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// Helper function to check if employer name contains "mvest" (case insensitive, with brackets, etc.)
function isMvestEmployer(employerName, memberNumber) {
  if (employerName) {
    const normalizedName = employerName.toLowerCase();
    if (
      normalizedName.includes("mvest") ||
      normalizedName.includes("(mvest)") ||
      normalizedName.includes("[mvest]") ||
      normalizedName.includes(" mvest ") ||
      normalizedName.includes(" mvest)") ||
      normalizedName.includes("(mvest ") ||
      normalizedName.includes(" mvest]") ||
      normalizedName.includes("[mvest ")
    ) return true;
  }
  if (memberNumber) {
    const decoded = memberNumber.includes('%') ? decodeURIComponent(memberNumber) : memberNumber;
    if (/\/mv\//i.test(decoded)) return true;
  }
  return false;
}

async function downloadPdfWithAuth(url, filename) {
  try {
    const token = getUserToken();
    if (!token) {
      notifyUser(
        "error",
        "Please log in to download documents",
        "Authentication Required",
      );
      return;
    }

    // Ensure we use HTTPS from the start to avoid redirect issues
    let secureUrl = url;
    if (url.startsWith("http://")) {
      secureUrl = url.replace("http://", "https://");
    }

    const response = await fetch(secureUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "premium-statement.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download error:", error);
    notifyUser("error", "Failed to download document", "Download Error");
  }
}

function appendAuthTokenToUrl(url) {
  if (!url) return url;
  const token = getUserToken();
  if (!token) return url;

  // Replace any existing token parameter with the session token
  let result = url.replace(/token=[^&]*/, `token=${token}`);

  // If no token parameter existed, add it
  if (!result.includes("token=")) {
    const separator = result.includes("?") ? "&" : "?";
    result += `${separator}token=${token}`;
  }

  // Ensure download=true is present
  if (!result.includes("download=")) {
    const separator = result.includes("?") ? "&" : "?";
    result += `${separator}download=true`;
  }

  return result;
}

function PensionProductDetails({ productDetails, beneficiaries }) {
  const [showModal, setShowModal] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [activeBeneficiary, setActiveBeneficiary] = useState(null);
  const [futureValueModalOpen, setFutureValueModalOpen] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [investmentReportModalOpen, setInvestmentReportModalOpen] =
    useState(false);
  const [premiumStatementModalOpen, setPremiumStatementModalOpen] =
    useState(false);
  const [paymentOptionsModalOpen, setPaymentOptionsModalOpen] = useState(false);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [factsheetModalOpen, setFactsheetModalOpen] = useState(false);
  const [pensionStatementModalOpen, setPensionStatementModalOpen] = useState(false);
  const [pensionReports, setPensionReports] = useState([]);
  const [generatingPensionStmt, setGeneratingPensionStmt] = useState(false);
  const [loadingPensionReports, setLoadingPensionReports] = useState(false);
  const [selectedPremiumYears, setSelectedPremiumYears] = useState([]);
  const [reportGenerated, setReportGenerated] = useState(false);
  const buttonContainerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  React.useEffect(() => {
    const container = buttonContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const el = buttonContainerRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const { mutate: deleteBeneficiary, isLoading: deletingBeneficiary } =
    useDeleteBeneficiary();

  const user = getUserDetail();
  const upgradeShownKey = `upgrade_banner_shown_${user?.member_number || user?.id || 'anonymous'}`;

  const [showUpgradeBanner, setShowUpgradeBanner] = useState(() => {
    return !localStorage.getItem(upgradeShownKey);
  });

  React.useEffect(() => {
    if (showUpgradeBanner) {
      localStorage.setItem(upgradeShownKey, 'true');
    }
  }, [showUpgradeBanner, upgradeShownKey]);

  // Normalize incoming productDetails shape to a predictable object `pd` used by this UI
  const pd = React.useMemo(() => {
    if (!productDetails)
      return {
        policyName: "",
        policyNumber: "",
        status: "",
        memberName: "",
        penTypeDescription: "",
        employerName: "",
        employerNumber: "",
        ssnitNumber: "",
        totalPremiumPaid: 0,
        modalPremium: 0,
        totalRedemption: 0,
        totalContributions: 0,
        accruedInterest: 0,
        schemeCurrentValue: 0,
        balanceBroughtForward: 0,
        premiumAmount: 0,
        premiumFrequency: "",
        startDate: "",
        endDate: "",
      };

    const statusRaw = (
      productDetails.status ||
      productDetails.Status ||
      ""
    ).toString();

    // Map only the fields present in the API response
    return {
      policyName: productDetails.policyName || "",
      policyNumber: productDetails.policyNumber || "",
      status: statusRaw,
      memberName: productDetails.memberName || "",
      penTypeDescription: productDetails.penTypeDescription || "",
      employerName: productDetails.employerName || "",
      employerNumber: productDetails.employerNumber || "",
      ssnitNumber: productDetails.ssnitNumber || "",
      totalPremiumPaid:
        typeof productDetails.totalPremiumPaid === "number"
          ? productDetails.totalPremiumPaid
          : 0,
      modalPremium:
        typeof productDetails.modalPremium === "number"
          ? productDetails.modalPremium
          : 0,
      premiumAmount:
        typeof productDetails.premiumAmount === "number"
          ? productDetails.premiumAmount
          : 0,
      premiumFrequency: productDetails.premiumFrequency || "",
      startDate: productDetails.startDate || "",
      endDate: productDetails.endDate || "",
      totalRedemption:
        typeof productDetails.totalRedemption === "number"
          ? productDetails.totalRedemption
          : 0,
      totalContributions:
        typeof productDetails.totalContributions === "number"
          ? productDetails.totalContributions
          : 0,
      accruedInterest:
        typeof productDetails.accruedInterest === "number"
          ? productDetails.accruedInterest
          : 0,
      schemeCurrentValue:
        typeof productDetails.schemeCurrentValue === "number"
          ? productDetails.schemeCurrentValue
          : 0,
      balanceBroughtForward:
        typeof productDetails.balanceBroughtForward === "number"
          ? productDetails.balanceBroughtForward
          : 0,
    };
  }, [productDetails]);

  console.log("Debug pd:", pd);

  // Decode URL-encoded staff number for display
  // Use pd.employerNumber from productDetails instead of user.employer_number
  const decodedStaffNumber = user?.member_number
    ? decodeURIComponent(user.member_number)
    : "";
  const decodedEmployerNumber = pd.employerNumber
    ? decodeURIComponent(pd.employerNumber)
    : "";

  const {
    data: certificateData,
    isLoading: certificateLoading,
    error: certificateError,
    refetch: refetchCertificate,
  } = useGetCertificate(
    decodedEmployerNumber,
    decodedStaffNumber,
    certificateModalOpen,
  );

  console.log("Debug certificate params:", {
    employer: decodedEmployerNumber,
    staff: decodedStaffNumber,
    modalOpen: certificateModalOpen,
  });

  const {
    data: investmentReportData,
    isLoading: investmentReportLoading,
    error: investmentReportError,
    refetch: refetchInvestmentReport,
  } = useGetInvestmentReport(
    pd.policyNumber,
    investmentReportModalOpen,
    "pensions",
  );

  // Generate year options (current year + 7 previous years)
  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const options = [{ value: 'all', label: 'All' }];
    for (let i = 0; i < 7; i++) {
      const year = (currentYear - i).toString();
      options.push({ value: year, label: year });
    }
    return options;
  }, []);

  // Policy reports mutation (first API call)
  const {
    mutate: initiatePolicyReport,
    isLoading: initiatingPolicyReport,
  } = usePolicyReports();

  // Fetch premium statement when modal opens (second API call)
  const {
    data: premiumStatementData,
    isLoading: premiumStatementLoading,
    error: premiumStatementError,
    refetch: refetchPremiumStatement,
  } = useGetPremiumStatement(pd.policyNumber, premiumStatementModalOpen);

  // Generate report button handler
  const handleGenerateReport = () => {
    console.log('handleGenerateReport called', { policyNumber: pd.policyNumber, selectedPremiumYears, yearOptions });
    if (pd.policyNumber && selectedPremiumYears.length > 0) {
      // Convert 'all' to all available year values
      const yearsToSend = selectedPremiumYears.includes('all')
        ? yearOptions.filter(opt => opt.value !== 'all').map(opt => opt.value)
        : selectedPremiumYears;

      console.log('sending years:', yearsToSend);

      // First API call: initiate report generation
      initiatePolicyReport(
        { policyNumber: pd.policyNumber, year: yearsToSend },
        {
          onSuccess: (response) => {
            console.log('initiatePolicyReport success:', response);
            setReportGenerated(true);
            // Second API call: fetch generated reports
            refetchPremiumStatement();
          },
          onError: (error) => {
            console.error('initiatePolicyReport error:', error);
            // Still try to fetch existing reports even if initiation fails
            refetchPremiumStatement();
          },
        }
      );
    }
  };

  const {
    data: policyDocData,
    isLoading: policyDocLoading,
    error: policyDocError,
    refetch: refetchPolicyDoc,
  } = useGetPolicyDocument(pd.policyNumber, showModal, "pensions");

  React.useEffect(() => {
    if (certificateModalOpen) {
      refetchCertificate();
    }
  }, [certificateModalOpen, refetchCertificate]);

  React.useEffect(() => {
    if (investmentReportModalOpen) {
      refetchInvestmentReport();
    }
  }, [investmentReportModalOpen, refetchInvestmentReport]);

  // Fetch premium statement is auto-triggered by useGetPremiumStatement when modal opens (enabled flag)

  React.useEffect(() => {
    if (showModal) {
      refetchPolicyDoc();
    }
  }, [showModal, refetchPolicyDoc]);

  React.useEffect(() => {
    if (
      certificateData &&
      certificateData.data &&
      certificateData.data.success === false
    ) {
      notifyUser("error", certificateData.data.message);
    }
  }, [certificateData]);

  React.useEffect(() => {
    if (
      investmentReportData &&
      investmentReportData.data &&
      investmentReportData.data.success === false
    ) {
      notifyUser("error", investmentReportData.data.message);
    }
  }, [investmentReportData]);

  React.useEffect(() => {
    if (premiumStatementData && premiumStatementData.success === false) {
      notifyUser(
        "error",
        premiumStatementData.data?.message ||
          "Failed to load premium statement",
      );
    }
  }, [premiumStatementData]);

  const handleGeneratePensionStatement = async () => {
    setGeneratingPensionStmt(true);
    try {
      const response = await api.get('/pensions/statement');
      const downloadUrl = response?.data?.data?.download_url;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
        message.success('Statement generated successfully');
      } else {
        message.error('Statement generation failed. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate pension statement', err);
      message.error('Failed to generate statement.');
    } finally {
      setGeneratingPensionStmt(false);
      fetchPensionReports();
    }
  };

  const fetchPensionReports = async () => {
    setLoadingPensionReports(true);
    try {
      const response = await api.get('/reports');
      const allReports = response?.data?.data || [];
      const pensionStmts = allReports.filter(r => r.type === 'pensions statement');
      setPensionReports(pensionStmts);
    } catch (err) {
      console.error('Failed to fetch pension reports', err);
    } finally {
      setLoadingPensionReports(false);
    }
  };

  React.useEffect(() => {
    if (pensionStatementModalOpen) {
      fetchPensionReports();
    }
  }, [pensionStatementModalOpen]);

  // Payment hooks
  const initiatePensionPayment = useInitiatePensionPayment();
  // Payment history hook - no filters initially, can be enhanced later
  const {
    data: pensionPaymentHistory,
    isLoading: paymentHistoryLoading,
    refetch: refetchPaymentHistory,
  } = usePensionPaymentHistory({});

  // Payment handler functions
  const handlePayNow = async (paymentData) => {
    try {
      const paymentRequestData = {
        amount: paymentData.amount,
        currency: paymentData.currency || "GHS",
      };

      const result =
        await initiatePensionPayment.mutateAsync(paymentRequestData);

      if (result?.success && result?.data?.checkoutUrl) {
        // Redirect to payment checkout
        window.open(result.data.checkoutUrl, "_blank");
        notifyUser("success", "Payment initiated successfully", "Payment");
      } else {
        notifyUser("error", "Failed to initiate payment", "Payment Error");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      notifyUser(
        "error",
        error.message || "Failed to initiate payment",
        "Payment Error",
      );
    }
  };

  const handleViewPaymentHistory = () => {
    setPaymentHistoryModalOpen(true);
    // Refresh payment history when opening modal
    refetchPaymentHistory();
  };

  const normalizedBeneficiaries = React.useMemo(() => {
    if (!beneficiaries) return [];
    if (Array.isArray(beneficiaries))
      return normalizeBeneficiaries(beneficiaries);
    return normalizeBeneficiaries(
      extractBeneficiaryListFromResponse(beneficiaries),
    );
  }, [beneficiaries]);

  const [currentBeneficiaries, setCurrentBeneficiaries] = useState(
    normalizedBeneficiaries,
  );

  React.useEffect(() => {
    setCurrentBeneficiaries(normalizedBeneficiaries);
  }, [normalizedBeneficiaries]);

  const currentTotalPerc = currentBeneficiaries.reduce(
    (sum, b) => sum + (b.percAllocValue || 0),
    0,
  );

  React.useEffect(() => {
    if (premiumStatementModalOpen) {
      setSelectedPremiumYears(['all']);
    }
  }, [premiumStatementModalOpen]);

  const handleOpenEdit = (beneficiary) => {
    setActiveBeneficiary(beneficiary);
    setIsEdit(true);
    setAddModalOpen(true);
  };

  const handleOpenDelete = (beneficiary) => {
    setActiveBeneficiary(beneficiary);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setActiveBeneficiary(null);
  };

  const confirmDelete = () => {
    if (!activeBeneficiary) return;
    deleteBeneficiary(
      { beneficiaryId: activeBeneficiary.beneficiaryId },
      {
        onSuccess: () => {
          setCurrentBeneficiaries((prev) =>
            prev.filter(
              (beneficiary) =>
                beneficiary.beneficiaryId !== activeBeneficiary.beneficiaryId,
            ),
          );
          closeDeleteModal();
        },
      },
    );
  };

  return (
    <div
      className="pensions-details-page"
      style={{
        minHeight: "80vh",
        background: "#f5f6fa",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
      }}
    >
      <style>
        {`
          .button-card {
            transition: transform 0.3s ease;
          }
          .button-card:hover {
            transform: scale(1.05);
          }
          .button-container::-webkit-scrollbar {
            display: none;
          }
          .button-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .tab-scroll-btn:disabled {
            pointer-events: none;
            opacity: 0;
          }
          .tab-scroll-btn:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.08);
            box-shadow: 0 12px 28px rgba(0, 176, 80, 0.45);
          }
          @media (max-width: 768px) {
            .product-details-wrapper {
              max-width: 100% !important;
              padding: 0 8px !important;
              gap: 12px !important;
            }
            .product-details-card {
              max-width: 100% !important;
              padding: 20px 12px !important;
              border-radius: 12px !important;
              overflow: visible !important;
            }
            .product-details-card > div[style*="overflow: hidden"] {
              overflow: visible !important;
            }
            .button-card {
              min-width: 56px !important;
              max-width: 56px !important;
              padding: 3px 2px !important;
              gap: 3px !important;
            }
            .button-card img {
              width: 14px !important;
              height: 14px !important;
            }
            .button-card .anticon {
              font-size: 14px !important;
            }
            .button-card span {
              font-size: 7px !important;
              line-height: 1.1 !important;
            }
            .tab-scroll-btn {
              display: none !important;
            }
            .pensions-details-page {
              padding: 12px 8px !important;
              gap: 12px !important;
            }
            .policy-modal-content {
              padding: 0 16px 16px 16px !important;
            }
            .policy-modal-header {
              padding: 16px 16px 0 16px !important;
            }
            .policy-doc-meta {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 8px !important;
            }
            .policy-doc-actions {
              margin-left: 0 !important;
              width: 100% !important;
            }
            .policy-doc-actions .btn {
              width: 100% !important;
            }
          }
        `}
      </style>
      {showUpgradeBanner && (
        <RecommendUpgrade
          currentPremium={pd.premiumAmount || 0}
          suggestedPremium={(pd.premiumAmount || 0) * 1.1}
          additionalBenefit={(pd.premiumAmount || 0) * 0.1}
          onUpgrade={() => {
            console.log(
              "Upgrade initiated for pension product:",
              pd.policyNumber,
            );
          }}
          onNotInterested={() => {
            console.log(
              "User not interested in upgrade for pension product:",
              pd.policyNumber,
            );
          }}
        />
      )}

      <div
        className="product-details-wrapper"
        style={{
          width: "100%",
          maxWidth: 700,
          display: "flex",
          flexDirection: "column",
          gap: 24,
          alignItems: "center",
        }}
      >
        {/* Product Details Card */}
        <div
          className="product-details-card"
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            padding: "32px 48px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: 700,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              width: "100%",
            }}
          >
            <span style={{ fontWeight: 800, fontSize: 20, color: "#000000ff" }}>
              {pd.policyName}
            </span>
            <div
              style={{
                position: "relative",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <div
                className="buttons-scroll-wrapper"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <button
                  type="button"
                  className="tab-scroll-btn"
                  aria-label="Scroll buttons left"
                  onClick={() => {
                    const container = buttonContainerRef.current;
                    if (container)
                      container.scrollBy({ left: -120, behavior: "smooth" });
                  }}
                  disabled={!canScrollLeft}
                  style={{
                    opacity: canScrollLeft ? 1 : 0,
                    position: "relative",
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#00b050",
                    boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)",
                    cursor: "pointer",
                    transition: "opacity 0.2s, transform 0.2s, box-shadow 0.2s",
                    zIndex: 30,
                    color: "#fff",
                    marginRight: 8,
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
                <div
                  className="button-container"
                  ref={buttonContainerRef}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    gap: 10,
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  {/* Only show Pay Now button for MVEST employers */}
                  {isMvestEmployer(pd.employerName, user?.member_number) && (
                    <div
                      className="button-card"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        cursor: "pointer",
                        minWidth: 100,
                        flexShrink: 0,
                      }}
                      onClick={() => setPaymentOptionsModalOpen(true)}
                    >
                      <img
                        src={payIcon}
                        alt="Pay Now"
                        style={{ width: 24, height: 24 }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#00704A",
                        }}
                      >
                        Pay Now
                      </span>
                    </div>
                  )}
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setCertificateModalOpen(true)}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 24, color: "#00704A" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Certificates
                    </span>
                  </div>
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setFactsheetModalOpen(true)}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 24, color: "#00704A" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      FactSheets
                    </span>
                  </div>
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setPensionStatementModalOpen(true)}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 24, color: "#00704A" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Pension Statement
                    </span>
                  </div>
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setFutureValueModalOpen(true)}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 24, color: "#00704A" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Future Value Calculator
                    </span>
                  </div>
                  <div
                    className="button-card"
                    style={{
                      display: "none",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => {
                      if (
                        process.env.REACT_APP_COMING_SOON_BUTTONS === "true"
                      ) {
                        notifyUser("info", "Coming Soon!", "Info");
                      } else {
                        setClaimModalOpen(true);
                      }
                    }}
                  >
                    <img
                      src={claimIcon}
                      alt="Claim"
                      style={{ width: 24, height: 24 }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Claim
                    </span>
                  </div>
                  <div
                    className="button-card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      cursor: "pointer",
                      minWidth: 100,
                      flexShrink: 0,
                    }}
                    onClick={() => setAddModalOpen(true)}
                  >
                    <img
                      src={addBeneIcon}
                      alt="Add Beneficiary"
                      style={{ width: 24, height: 24 }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        textAlign: "center",
                        fontWeight: 600,
                        color: "#00704A",
                      }}
                    >
                      Add Beneficiary
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="tab-scroll-btn"
                  aria-label="Scroll buttons right"
                  onClick={() => {
                    const container = buttonContainerRef.current;
                    if (container)
                      container.scrollBy({ left: 120, behavior: "smooth" });
                  }}
                  disabled={!canScrollRight}
                  style={{
                    opacity: canScrollRight ? 1 : 0,
                    position: "relative",
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#00b050",
                    boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)",
                    cursor: "pointer",
                    transition: "opacity 0.2s, transform 0.2s, box-shadow 0.2s",
                    zIndex: 30,
                    color: "#fff",
                    marginLeft: 8,
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
          <div style={{ height: 18 }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Member Number */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.policyNumber}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Member Number
              </div>
            </div>

            {/* Pension Status */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{ color: "#000000ff", fontWeight: 500, fontSize: 18 }}
                >
                  {pd.status
                    ? pd.status.charAt(0).toUpperCase() + pd.status.slice(1)
                    : "Unknown"}
                </span>
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Scheme Status
              </div>
            </div>

            {/* Member Name */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.memberName}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Member Name
              </div>
            </div>

            {/* Pension Type */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.penTypeDescription}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Pension Type
              </div>
            </div>

            {/* Employer Name */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.employerName}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Employer Name
              </div>
            </div>

            {/* Employer Number */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.employerNumber}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Employer Number
              </div>
            </div>

            {/* SSNIT Number */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.ssnitNumber || "N/A"}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                SSNIT Number
              </div>
            </div>

            {/* Total Premium Amount */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.totalPremiumPaid)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Total Premium Amount
              </div>
            </div>


            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.modalPremium)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
            Premium Amount
              </div>
            </div>

            {/* Total Contributions */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.totalContributions)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Total Contributions
              </div>
            </div>

            {/* Total Redemption */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.totalRedemption)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Total Redemption
              </div>
            </div>

            {/* Accrued Interest */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.accruedInterest)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Accrued Interest
              </div>
            </div>

            {/* Scheme Current Value */}
             <div
               style={{
                 display: 'none'
               }}
             >
               <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                 {formatCurrency(pd.schemeCurrentValue)}
               </div>
               <div
                 style={{
                   color: "#888",
                   fontWeight: 500,
                   fontSize: 13,
                   marginTop: 2,
                 }}
               >
                 Scheme Current Value
               </div>
             </div>

             {/* Balance Brought Forward */}
             <div
               style={{
                 display: 'none'
               }}
             >
               <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                 {formatCurrency(pd.balanceBroughtForward)}
               </div>
               <div
                 style={{
                   color: "#888",
                   fontWeight: 500,
                   fontSize: 13,
                   marginTop: 2,
                 }}
               >
                 Balance Brought Forward
               </div>
             </div>

            {/* Monthly Premium */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(pd.premiumAmount)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Monthly Premium
              </div>
            </div>

            {/* Premium Frequency */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {pd.premiumFrequency}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Premium Frequency
              </div>
            </div>

            {/* Premium Start Date */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.startDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Scheme Start Date
              </div>
            </div>

            {/* Premium End Date */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: "10px 0",
                textAlign: "left",
              }}
            >
              <div style={{ color: "#444", fontWeight: 700, fontSize: 18 }}>
                {formatDate(pd.endDate)}
              </div>
              <div
                style={{
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                Scheme End Date
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Recommendation Card */}

        {/* Modal for Policy Documents */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 18,
                minWidth: 320,
                maxWidth: 700,
                width: "100%",
                boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
                padding: "32px 32px 24px 32px",
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{pd.policyName}</span>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  marginBottom: 18,
                  marginTop: 0,
                }}
              >
                See all policy documents available
              </p>

              {/* Document List */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 18 }}
              >
                {policyDocLoading ? (
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <Spin />
                  </div>
                ) : policyDocError ? (
                  <div style={{ color: "red" }}>
                    {(() => {
                      const message =
                        policyDocError?.response?.data?.message ||
                        policyDocError?.message ||
                        "Failed to load policy document";
                      return message;
                    })()}
                  </div>
                ) : policyDocData?.success ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 18,
                    }}
                  >
                    <div
                      style={{
                        background: "#f8f9fa",
                        borderRadius: 10,
                        padding: "18px 18px 14px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: 16,
                          marginBottom: 2,
                        }}
                      >
                        Policy Document
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            color: "#888",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Policy Document PDF
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#ccc",
                              margin: "0 8px",
                              verticalAlign: "middle",
                            }}
                          ></span>
                          application/pdf
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexShrink: 0,
                            marginLeft: "auto",
                          }}
                        >
                          <button
                            className="btn btn-primary"
                            style={{
                              padding: "4px 14px",
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                            onClick={() =>
                              window.open(
                                appendAuthTokenToUrl(policyDocData.data.url),
                                "_blank",
                              )
                            }
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px 0",
                      color: "#888",
                    }}
                  >
                    No policy document available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Beneficiary Card */}
        <div
          className="product-details-card"
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
            padding: "32px 48px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: 700,
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: 20,
              color: "#000000ff",
              marginBottom: 12,
            }}
          >
            {currentBeneficiaries && currentBeneficiaries.length === 1
              ? "Beneficiary"
              : "Beneficiaries"}
          </div>
          <div className="beneficiary-scroll" style={{ display: "flex", flexDirection: "column", gap: 0, maxHeight: 400, overflowY: "auto", scrollbarGutter: "stable" }}>
            {!currentBeneficiaries || currentBeneficiaries.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "32px 0",
                  color: "#888",
                }}
              >
                {/* Multi-user/group icon SVG */}
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: 16 }}
                >
                  <circle cx="30" cy="30" r="30" fill="#f5f6fa" />
                  {/* Main user */}
                  <ellipse cx="30" cy="27" rx="10" ry="10" fill="#d9d9d9" />
                  {/* Left user */}
                  <ellipse cx="18" cy="32" rx="6" ry="6" fill="#e0e0e0" />
                  {/* Right user */}
                  <ellipse cx="42" cy="32" rx="6" ry="6" fill="#e0e0e0" />
                  {/* Group base */}
                  <ellipse cx="30" cy="46" rx="17" ry="8" fill="#d9d9d9" />
                  <ellipse cx="18" cy="46" rx="8" ry="4" fill="#e0e0e0" />
                  <ellipse cx="42" cy="46" rx="8" ry="4" fill="#e0e0e0" />
                </svg>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 17,
                    color: "#888",
                    textAlign: "center",
                  }}
                >
                  No beneficiaries assigned to this policy
                </div>
              </div>
            ) : (
              currentBeneficiaries.map((b, idx) => {
                const initials = getInitials(b.name);
                const split = formatBeneficiaryPercentage(b);
                return (
                  <div
                    className="beneficiary-row"
                    key={b.beneficiaryId || b.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      borderTop: idx !== 0 ? "1px solid #eee" : undefined,
                      padding: "18px 0",
                      textAlign: "left",
                      position: "relative",
                      transition: "background 0.2s, border-radius 0.2s",
                      cursor: "pointer",
                      borderRadius: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e6f9ed";
                      e.currentTarget.style.borderRadius = "12px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "";
                      e.currentTarget.style.borderRadius = "0";
                    }}
                  >
                    {/* Profile left */}
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: "#111",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#fff",
                        fontSize: 20,
                        marginRight: 18,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    {/* Details center */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          color: "#222",
                          fontSize: 18,
                          lineHeight: 1.2,
                        }}
                      >
                        {b.name}
                      </div>
                      <div
                        style={{
                          color: "#888",
                          fontWeight: 500,
                          fontSize: 15,
                          marginTop: 2,
                        }}
                      >
                        {split} Split
                      </div>
                      <div
                        style={{
                          color: "#888",
                          fontWeight: 500,
                          fontSize: 15,
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                        }}
                      >
                        <span>{b.relationship}</span>
                        {b.dob ? (
                          <>
                            <span
                              style={{
                                fontSize: 17,
                                fontWeight: 400,
                                color: "#bbb",
                              }}
                            >
                              -
                            </span>
                            <span>{formatDate(b.dob)}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        type="button"
                        className="btn btn-white"
                        aria-label="Edit beneficiary"
                        style={{
                          padding: "6px 12px",
                          fontSize: 16,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenEdit(b);
                        }}
                      >
                        <EditOutlined />
                      </button>
                      <button
                        type="button"
                        className="btn btn-white"
                        aria-label="Delete beneficiary"
                        style={{
                          padding: "6px 12px",
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#C62828",
                          borderColor: "#C62828",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenDelete(b);
                        }}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
            <style>{`
              .beneficiary-row { width: 100%; }
              .beneficiary-row div { box-sizing: border-box; }
              .beneficiary-scroll { scrollbar-width: thin; scrollbar-color: #ccc transparent; }
              .beneficiary-scroll::-webkit-scrollbar { width: 5px; }
              .beneficiary-scroll::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
              .beneficiary-scroll::-webkit-scrollbar-track { background: transparent; }
              @media (max-width: 640px) {
                .beneficiary-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px; }
                .beneficiary-row > div:first-child { width: 40px !important; height: 40px !important; font-size: 16px !important; margin-right: 12px !important; }
                .beneficiary-row .btn { padding: 8px 10px !important; font-size: 13px !important; }
                .beneficiary-row > div:last-child { align-self: flex-end !important; margin-top: 4px !important; }
              }
            `}</style>
          </div>
        </div>
      </div>

      {deleteModalOpen && activeBeneficiary && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              width: "100%",
              maxWidth: 460,
              padding: "28px 32px",
              boxShadow: "0 16px 60px rgba(0,0,0,0.15)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>
              Remove Beneficiary
            </div>
            <p style={{ color: "#666", fontSize: 15, lineHeight: 1.4 }}>
              Are you sure you want to remove{" "}
              <strong>{activeBeneficiary.name}</strong> from this policy? This
              action cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                className="btn btn-white"
                onClick={closeDeleteModal}
                style={{ padding: "10px 18px", fontWeight: 600 }}
              >
                Keep Beneficiary
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  padding: "10px 18px",
                  fontWeight: 600,
                  background: "#C62828",
                  color: "#fff",
                  borderColor: "#C62828",
                }}
                onClick={confirmDelete}
                disabled={deletingBeneficiary}
              >
                {deletingBeneficiary ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <AddBeneficiaryModal
        isOpen={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setIsEdit(false);
          setActiveBeneficiary(null);
        }}
        currentTotalPerc={currentTotalPerc}
        isEdit={isEdit}
        beneficiaryToEdit={activeBeneficiary}
        onEditSuccess={(updatedBeneficiary) => {
          setCurrentBeneficiaries((prev) =>
            prev.map((b) =>
              b.beneficiaryId === updatedBeneficiary.beneficiaryId
                ? updatedBeneficiary
                : b,
            ),
          );
        }}
      />
      {certificateModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setCertificateModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              minWidth: 320,
              maxWidth: 700,
              width: "100%",
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
              padding: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="policy-modal-header"
              style={{ padding: "24px 32px 0 32px" }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Membership Certificate</span>
                <button
                  onClick={() => setCertificateModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  marginBottom: 18,
                  marginTop: 0,
                }}
              >
                Download your membership certificate
              </p>
            </div>

            {/* Scrollable body */}
            <div
              className="policy-modal-content"
              style={{
                padding: "0 32px 24px 32px",
                overflowY: "auto",
                paddingBottom: 32,
              }}
            >
              {certificateLoading ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Spin />
                </div>
              ) : certificateError ? (
                <div style={{ color: "red" }}>
                  {(() => {
                    const message =
                      certificateError?.response?.data?.message ||
                      certificateError?.message ||
                      "Failed to load certificate";
                    return message;
                  })()}
                </div>
              ) : certificateData?.success &&
                certificateData?.data &&
                certificateData.data.success !== false ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 18 }}
                >
                  <div
                    style={{
                      background: "#f8f9fa",
                      borderRadius: 10,
                      padding: "18px 18px 14px 18px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#222",
                        fontSize: 16,
                        marginBottom: 2,
                        wordBreak: "break-all",
                        overflowWrap: "break-word",
                      }}
                    >
                      {certificateData.data.fileName}
                    </div>
                    <div
                      className="policy-doc-meta"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          color: "#888",
                          fontSize: 13,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Certificate PDF
                        <span
                          style={{
                            display: "inline-block",
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: "#ccc",
                            margin: "0 8px",
                            verticalAlign: "middle",
                          }}
                        ></span>
                        {certificateData.data.mimeType}
                      </div>
                      <div
                        className="policy-doc-actions"
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexShrink: 0,
                          marginLeft: "auto",
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          style={{
                            padding: "4px 14px",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                          onClick={() => {
                            try {
                              const {
                                fileName,
                                mimeType,
                                data: base64Data,
                              } = certificateData.data;
                              // Handle base64url encoding
                              let base64 = base64Data
                                .replace(/-/g, "+")
                                .replace(/_/g, "/");
                              // Add padding if needed
                              while (base64.length % 4) {
                                base64 += "=";
                              }
                              const byteCharacters = atob(base64);
                              const byteNumbers = new Array(
                                byteCharacters.length,
                              );
                              for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                              }
                              const byteArray = new Uint8Array(byteNumbers);
                              const blob = new Blob([byteArray], {
                                type: mimeType,
                              });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = fileName;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error(
                                "Error decoding certificate:",
                                error,
                              );
                              notifyUser(
                                "error",
                                "Failed to download certificate. The file data may be corrupted.",
                                "Download Error",
                              );
                            }
                          }}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : certificateData?.success &&
                certificateData?.data?.success === false ? (
                <div style={{ color: "red" }}>
                  {certificateData.data.message || "Certificate not available."}
                </div>
              ) : (
                <div style={{ color: "#666" }}>No certificate available.</div>
              )}
            </div>
          </div>
        </div>
      )}
      {investmentReportModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setInvestmentReportModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              minWidth: 320,
              maxWidth: 700,
              width: "100%",
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
              padding: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: "24px 32px 0 32px" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Investment Report</span>
                <button
                  onClick={() => setInvestmentReportModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  marginBottom: 18,
                  marginTop: 0,
                }}
              >
                Download your investment report
              </p>
            </div>

            {/* Scrollable body */}
            <div
              style={{
                padding: "0 32px 24px 32px",
                overflowY: "auto",
                paddingBottom: 32,
              }}
            >
              {investmentReportLoading ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Spin />
                </div>
              ) : investmentReportError ? (
                <div style={{ color: "red" }}>
                  {(() => {
                    const message =
                      investmentReportError?.response?.data?.message ||
                      investmentReportError?.message ||
                      "Failed to load investment report";
                    return message;
                  })()}
                </div>
              ) : investmentReportData?.success &&
                investmentReportData?.data ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 18 }}
                >
                  <div
                    style={{
                      background: "#f8f9fa",
                      borderRadius: 10,
                      padding: "18px 18px 14px 18px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#222",
                        fontSize: 16,
                        marginBottom: 2,
                      }}
                    >
                      Investment_Report.pdf
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          color: "#888",
                          fontSize: 13,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Investment Report PDF
                        <span
                          style={{
                            display: "inline-block",
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: "#ccc",
                            margin: "0 8px",
                            verticalAlign: "middle",
                          }}
                        ></span>
                        application/pdf
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexShrink: 0,
                          marginLeft: "auto",
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          style={{
                            padding: "4px 14px",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                          onClick={() => {
                            try {
                              const base64Data = investmentReportData.data;
                              let base64 = base64Data
                                .replace(/-/g, "+")
                                .replace(/_/g, "/");
                              while (base64.length % 4) {
                                base64 += "=";
                              }
                              const byteCharacters = atob(base64);
                              const byteNumbers = new Array(
                                byteCharacters.length,
                              );
                              for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                              }
                              const byteArray = new Uint8Array(byteNumbers);
                              const blob = new Blob([byteArray], {
                                type: "application/pdf",
                              });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = "Investment_Report.pdf";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error(
                                "Error decoding investment report:",
                                error,
                              );
                              notifyUser(
                                "error",
                                "Failed to download investment report. The file data may be corrupted.",
                                "Download Error",
                              );
                            }
                          }}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color: "#666" }}>
                  No investment report available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {premiumStatementModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setPremiumStatementModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              minWidth: 320,
              maxWidth: 700,
              width: "100%",
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
              padding: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: "24px 32px 0 32px" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Premium Statement</span>
                <button
                  onClick={() => setPremiumStatementModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  marginBottom: 18,
                  marginTop: 0,
                }}
              >
                Download your premium statement
              </p>
            </div>

{/* Scrollable body */}
            <div
              style={{
                padding: "0 32px 24px 32px",
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
                paddingBottom: 32,
              }}
            >
              {/* Year Selector */}
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#444",
                    marginBottom: 8,
                  }}
                >
                  Select Year(s)
                </label>
                <Select
                  mode="multiple"
                  placeholder="Select years"
                  value={selectedPremiumYears}
                  onChange={(value) => {
                    if (value.length === 0) return;
                    if (value.includes('all')) {
                      setSelectedPremiumYears(['all']);
                    } else if (selectedPremiumYears.includes('all') && value.length > 0 && !value.includes('all')) {
                      setSelectedPremiumYears(value.filter(v => v !== 'all'));
                    } else {
                      setSelectedPremiumYears(value);
                    }
                  }}
                  disabled={initiatingPolicyReport || premiumStatementLoading}
                  options={yearOptions}
                  style={{ width: "100%", marginBottom: 12 }}
                  dropdownStyle={{ zIndex: 10000 }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />

                {/* Generate Button - Always visible below dropdown */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGenerateReport}
                  disabled={
                    selectedPremiumYears.length === 0 ||
                    initiatingPolicyReport ||
                    premiumStatementLoading
                  }
                >
                  {initiatingPolicyReport ? (
                    <>
                      <Spin size="small" />
                      Generating...
                    </>
                  ) : (
                    <>Generate Report</>
                  )}
                </button>
              </div>

              {premiumStatementLoading || initiatingPolicyReport ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Spin />
                  <p style={{ color: "#666", fontSize: 14, marginTop: 12 }}>
                    {initiatingPolicyReport ? "Generating report..." : "Loading..."}
                  </p>
                </div>
              ) : premiumStatementError ? (
                <div style={{ color: "red" }}>
                  {(() => {
                    const message =
                      premiumStatementError?.response?.data?.message ||
                      premiumStatementError?.message ||
                      "Failed to load premium statement";
                    return message;
                  })()}
                </div>
              ) : premiumStatementData?.success &&
                premiumStatementData?.data?.length > 0 ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {premiumStatementData.data
                    .filter(report => (report.status === "completed" || report.status === "downloaded") && report.download_url)
                    .map((report) => (
                      <div
                        key={report.id}
                        style={{
                          background: "#f8f9fa",
                          borderRadius: 10,
                          padding: "18px 18px 14px 18px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            color: "#666",
                            fontSize: 13,
                            marginBottom: 4,
                          }}
                        >
                          Generated: {formatDate(report.created_at)}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 18,
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              color: "#888",
                              fontSize: 13,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Premium Statement PDF
                            <span
                              style={{
                                display: "inline-block",
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "#ccc",
                                margin: "0 8px",
                                verticalAlign: "middle",
                              }}
                            ></span>
                            application/pdf
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexShrink: 0,
                              marginLeft: "auto",
                            }}
                          >
                            <button
                              className="btn btn-primary"
                              style={{
                                padding: "4px 14px",
                                fontWeight: 600,
                                fontSize: 14,
                              }}
                              onClick={() =>
                                downloadPdfWithAuth(
                                  report.download_url,
                                  `premium-statement-${report.id}.pdf`,
                                )
                              }
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  {premiumStatementData.data.filter(r => (r.status === "completed" || r.status === "downloaded") && r.download_url).length === 0 && (
                    <div style={{ color: "#666", textAlign: "center", padding: 24 }}>
                      No completed premium statements available.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: "#666" }}>
                  No premium statement available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {pensionStatementModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setPensionStatementModalOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              minWidth: 320,
              maxWidth: 700,
              width: "100%",
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
              padding: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px 32px 0 32px" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Pension Statement</span>
                <button
                  onClick={() => setPensionStatementModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  color: "#888",
                  fontSize: 15,
                  marginBottom: 4,
                  marginTop: 0,
                }}
              >
                View and download your pension statement
              </p>
              <p
                style={{
                  color: "#444",
                  fontSize: 14,
                  marginBottom: 18,
                  marginTop: 0,
                }}
              >
                Scheme: {pd.policyName}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                <button
                  className="btn btn-primary"
                  style={{
                    padding: "8px 32px",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                  onClick={handleGeneratePensionStatement}
                  disabled={generatingPensionStmt}
                >
                  {generatingPensionStmt ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Spin size="small" /> Generating...
                    </span>
                  ) : 'Generate Statement'}
                </button>
              </div>
            </div>
            <div
              style={{
                padding: "0 32px 24px 32px",
                overflowY: "auto",
                paddingBottom: 32,
              }}
            >
              {loadingPensionReports ? (
                <div style={{ padding: 24, textAlign: "center" }}>
                  <Spin />
                  <p style={{ color: "#666", fontSize: 14, marginTop: 12 }}>
                    Loading reports...
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#444", marginBottom: 4 }}>
                    Statements Generated
                  </div>
                  {pensionReports.filter(r => r.status === "completed" && r.download_url).length > 0 ? (
                    pensionReports
                      .filter(r => r.status === "completed" && r.download_url)
                      .map(report => (
                        <div
                          key={report.id}
                          style={{
                            background: "#f8f9fa",
                            borderRadius: 10,
                            padding: "14px 18px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <div style={{ color: "#666", fontSize: 13 }}>
                            Generated: {formatDate(report.created_at)}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 18,
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              style={{
                                color: "#888",
                                fontSize: 13,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Pension Statement PDF
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: "#ccc",
                                  margin: "0 8px",
                                  verticalAlign: "middle",
                                }}
                              ></span>
                              application/pdf
                            </div>
                            <span
                              style={{
                                color: "#00704A",
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(report.download_url, '_blank')}
                            >
                              View PDF
                            </span>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div style={{ color: "#666", textAlign: "center", padding: 24 }}>
                      No pension statements available. Click Generate to create one.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
       <Modal
         open={reportGenerated}
         onOk={() => setReportGenerated(false)}
         onCancel={() => setReportGenerated(false)}
         footer={null}
         closable={true}
         style={{ top: "30%" }}
         width={400}
       >
         <div style={{ textAlign: "center", padding: "20px 0" }}>
           <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 48 }} />
           <div style={{ color: "#52c41a", fontWeight: 600, marginTop: 16, marginBottom: 8, fontSize: 18 }}>Report generated successfully!</div>
           <div style={{ color: "#666", fontSize: 14 }}>Close this modal and click Download below to save your PDF.</div>
         </div>
       </Modal>
       <ClaimsModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        policyNumber={pd.policyNumber}
        productType="PENSION"
        policyName={pd.policyName}
        availableCashValue={pd.schemeCurrentValue}
      />
      <WithdrawalRequestModal
        isOpen={withdrawalModalOpen}
        onClose={() => setWithdrawalModalOpen(false)}
        policyNumber={pd.policyNumber}
        productType="PENSION"
      />
      <FutureValueCalculatorModal
        isOpen={futureValueModalOpen}
        onClose={() => setFutureValueModalOpen(false)}
      />
      <SimpleFactsheetModal
        isOpen={factsheetModalOpen}
        onClose={() => setFactsheetModalOpen(false)}
      />

      {/* Payment Options Modal */}
      <PaymentOptionsModal
        isOpen={paymentOptionsModalOpen}
        onClose={() => setPaymentOptionsModalOpen(false)}
        onPayNow={handlePayNow}
        onViewHistory={handleViewPaymentHistory}
        productType="Pensions"
        totalAmount={pd.premiumAmount || pd.modalPremium || 0}
        currency="GHS"
        status={pd.status}
      />

      {/* Debug info */}
      {console.log("PensionsProductDetails - Payment modal props:", {
        premiumAmount: pd.premiumAmount,
        modalPremium: pd.modalPremium,
        totalAmount: pd.premiumAmount || pd.modalPremium || 0,
        policyNumber: pd.policyNumber,
      }) || null}

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={paymentHistoryModalOpen}
        onClose={() => setPaymentHistoryModalOpen(false)}
        payments={pensionPaymentHistory?.data || []}
        isLoading={paymentHistoryLoading}
        productType="Pensions"
      />
    </div>
  );
}

// Default export: wrapper that reads from location state for dynamic navigation
export default function PensionProductDetailsWrapper() {
  const location = useLocation();
  const { productDetails: stateProductDetails } = location.state || {};

  const user = getUserDetail();
  const { data: allPensionData, isLoading: isLoadingPension } = useAllPension();

  // Auto-select scheme from API when no productDetails in location state
  const productDetails = React.useMemo(() => {
    if (stateProductDetails) return stateProductDetails;
    if (!allPensionData?.data) return null;
    const schemes = Array.isArray(allPensionData.data) ? allPensionData.data : [];
    if (schemes.length === 0) return null;

    const memberNum = user?.member_number || '';
    const match = /\/mv\//i.test(memberNum)
      ? schemes.find(s => s.MemberNumber === memberNum) || schemes[0]
      : schemes[0];

    if (!match) return null;

    return {
      policyName: match.MasterSchemeDescription || '',
      policyNumber: match.MemberNumber || '',
      status: String(match.Status || match.status || '').toLowerCase().trim(),
      memberName: match.MemberName || '',
      penTypeDescription: match.PenTypeDescription || '',
      employerName: match.EmployerName || '',
      employerNumber: match.EmployerNumber || '',
      ssnitNumber: match.ssnitNumber || match.SSNITNumber || '',
      totalPremiumPaid: match.SchemeCurrentValue || 0,
      modalPremium: match.MonthlyContribution || 0,
      premiumAmount: match.MonthlyContribution || 0,
      premiumFrequency: 'Monthly',
      startDate: match.EffectiveDate || '',
      endDate: '',
      totalRedemption: 0,
      totalContributions: match.TotalContributions || match.totalContributions || 0,
      accruedInterest: match.AccruedInterest || match.accruedInterest || 0,
      schemeCurrentValue: match.SchemeCurrentValue || match.schemeCurrentValue || 0,
      balanceBroughtForward: match.BalanceBroughtForward || match.balanceBroughtForward || 0,
    };
  }, [stateProductDetails, allPensionData, user?.member_number]);

  const employerName = productDetails?.employerName || '';
  const productMemberNumber = productDetails?.policyNumber || '';
  const isMvest = isMvestEmployer(employerName, productMemberNumber);

  const selectScheme = useSelectScheme();
  const hasSelectedScheme = React.useRef(false);

  React.useEffect(() => {
    if (productDetails && user?.member_number && !hasSelectedScheme.current) {
      hasSelectedScheme.current = true;
      const memberNum = user.member_number.includes('%')
        ? decodeURIComponent(user.member_number)
        : user.member_number;
      selectScheme.mutate({ memberNumber: memberNum });
    }
  }, [productDetails, user?.member_number]);

  const { data: mvestBeneficiariesData } = useGetMVestBeneficiaries(
    isMvest && !!productDetails,
    productMemberNumber || user?.member_number,
  );

  const beneficiaries = React.useMemo(() => {
    if (isMvest && mvestBeneficiariesData) {
      const list = Array.isArray(mvestBeneficiariesData)
        ? mvestBeneficiariesData
        : mvestBeneficiariesData?.data || [];
      if (!Array.isArray(list)) return location.state?.beneficiaries || [];
      return list.map((b) => ({
        beneficiaryId: b.beneficiary_id || null,
        name: [b.firstname, b.OtherName].filter(Boolean).join(' ') || '',
        percentage: b.perc_alloc ? `${b.perc_alloc}%` : '',
        percAllocValue: Number(b.perc_alloc) || 0,
        relationship: b.Relationship || '',
        dob: b.birth_date || '',
        address: b.address || '',
      }));
    }
    return location.state?.beneficiaries || [];
  }, [isMvest, mvestBeneficiariesData, location.state?.beneficiaries]);

  if (!productDetails && !isLoadingPension) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 720 }}>
          <h3 style={{ marginBottom: 8 }}>No product selected</h3>
          <p style={{ color: "#666", marginBottom: 16 }}>
            Please open a product from the dashboard or products list to see the
            investment details.
          </p>
          <div>
            <a
              href="/dashboard"
              className="btn btn-white"
              style={{ marginRight: 8 }}
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!productDetails && isLoadingPension) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <PensionProductDetails
      productDetails={productDetails}
      beneficiaries={beneficiaries}
    />
  );
}

// Also export the component for direct use
export { PensionProductDetails };
